import { useMemo, useState } from 'react'
import type { Sensor, SensorDoc, SensorDocsList } from '../api/types'
import { sizeLabel } from '../lib/projectChat'
import { refOf } from '../lib/projectFiles'
import { searchTerms } from '../lib/stationSearch'
import { formatDateTime } from '../lib/time'
import { FileBadge, FileLinks } from './ProjectDocuments'
import { SensorPicker, type PickerMode } from './SensorPicker'
import './documents.css'

function matches(text: string, query: string): boolean {
  const hay = searchTerms(text).join(' ')
  return searchTerms(query).every((term) => hay.includes(term))
}

/** Every file on every sensor of the project's stations (the Sensor Docs
 *  tab), one row per file however many sensors hold it, and the way to send
 *  a datasheet to many sensors at once. Files attached on one sensor's
 *  Sensor info show here too. Only this project's sensors are counted. */
export function SensorDocs({ projectId, list, loadError, onChanged }: {
  projectId: number; list: SensorDocsList | undefined; loadError?: string; onChanged: () => void
}) {
  const [query, setQuery] = useState('')
  const [station, setStation] = useState('')
  const [view, setView] = useState<'file' | 'sensor'>('file')
  const [open, setOpen] = useState<Set<string>>(new Set())
  const [picker, setPicker] = useState<PickerMode | null>(null)
  const sensors = list?.sensors ?? []
  const docs = list?.docs ?? []
  const stations = useMemo(() => [...new Map(sensors.map((s) => [s.station.code, s.station.name]))], [sensors])

  const sensorFits = (ref: string) => {
    return !station || ref.startsWith(`${station}/`)
  }
  const shownDocs = docs.filter((d) => d.sensors.some((s) => sensorFits(s.ref))
    && matches(`${d.name} ${d.sensors.map((s) => s.name).join(' ')}`, query))
  const holders = useMemo(() => {
    const out = new Map<string, SensorDoc[]>()
    for (const d of docs) for (const s of d.sensors) out.set(s.ref, [...(out.get(s.ref) ?? []), d])
    return out
  }, [docs])
  const shownSensors = sensors.filter((s) => holders.has(refOf(s)) && sensorFits(refOf(s))
    && matches(`${s.name} ${s.model ?? ''} ${(holders.get(refOf(s)) ?? []).map((d) => d.name).join(' ')}`, query))
  const toggle = (key: string) => setOpen((now) => {
    const next = new Set(now)
    if (next.has(key)) next.delete(key); else next.add(key)
    return next
  })

  return (
    <div role="tabpanel" aria-label="Sensor Docs" className="pd-panel">
      <div className="pi-tools">
        <label className="field inline pd-search">
          <span className="visually-hidden">Search files or sensors</span>
          <input type="search" placeholder="Search files or sensors" value={query}
                 onChange={(e) => setQuery(e.target.value)} />
        </label>
        <label className="field inline">
          <span className="visually-hidden">Station</span>
          <select value={station} onChange={(e) => setStation(e.target.value)}>
            <option value="">All stations</option>
            {stations.map(([code, name]) => <option key={code} value={code}>{name}</option>)}
          </select>
        </label>
        <div className="segmented" role="group" aria-label="Show">
          <button type="button" aria-pressed={view === 'file'} onClick={() => setView('file')}>By file</button>
          <button type="button" aria-pressed={view === 'sensor'} onClick={() => setView('sensor')}>By sensor</button>
        </div>
        <span className="pd-grow" />
        {list?.can_manage && (
          <button type="button" className="button primary" disabled={sensors.length === 0}
                  onClick={() => setPicker({ kind: 'new' })}>Upload to sensors</button>
        )}
      </div>

      {list === undefined && (loadError
        ? <p className="error" role="alert">The sensor files could not be loaded: {loadError}</p>
        : <p className="muted">Loading…</p>)}
      {list && sensors.length === 0 && (
        <p className="pj-empty">This project's stations have no sensors yet.</p>
      )}
      {list && sensors.length > 0 && docs.length === 0 && (
        <p className="pj-empty">
          No sensor files yet.{list.can_manage ? ' Upload a datasheet to many sensors at once.' : ''}
        </p>
      )}
      {list && docs.length > 0 && (view === 'file' ? shownDocs : shownSensors).length === 0 && (
        <p className="pj-empty">Nothing matches.</p>
      )}

      {view === 'file' && shownDocs.length > 0 && (
        <div className="pd-table">
          <table className="grid">
            <thead>
              <tr><th scope="col">File</th><th scope="col">On sensors</th><th scope="col">Stations</th>
                <th scope="col">Added</th><th scope="col"><span className="visually-hidden">Actions</span></th></tr>
            </thead>
            <tbody>
              {shownDocs.map((doc) => {
                const many = doc.sensors.length > 1
                const isOpen = open.has(doc.key)
                return (
                  <tr key={doc.key}>
                    <td>
                      <span className="pd-name"><FileBadge family={doc.family} />{doc.name}</span>
                      <span className="muted sd-size"> {sizeLabel(doc.bytes)}</span>
                    </td>
                    <td>
                      {many ? (
                        <>
                          <span className="sd-chip">{doc.sensors.length} sensors</span>{' '}
                          <button type="button" className="link" aria-expanded={isOpen}
                                  onClick={() => toggle(doc.key)}>{isOpen ? 'Hide' : 'Show'}</button>
                          {isOpen && (
                            <ul className="sd-sensors">
                              {doc.sensors.map((s) => <li key={s.ref}>{s.name} <span className="muted">· {s.station.name}</span></li>)}
                            </ul>
                          )}
                        </>
                      ) : <span className="sd-chip is-one">{doc.sensors[0]?.name}</span>}
                    </td>
                    <td className="muted">{[...new Set(doc.sensors.map((s) => s.station.name))].join(', ')}</td>
                    <td className="muted">
                      {formatDateTime(doc.created_at)}, {doc.by}
                      {doc.via === 'sensor-info' && <><br />from Sensor info</>}
                    </td>
                    <td>
                      <span className="pd-actions">
                        <FileLinks name={doc.name} url={doc.urls.original} inline={doc.inline} />
                        {list?.can_manage && (
                          <button type="button" className="link" onClick={() => setPicker({ kind: 'change', doc })}>
                            Sensors…<span className="visually-hidden"> for {doc.name}</span>
                          </button>
                        )}
                      </span>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {view === 'sensor' && shownSensors.length > 0 && (
        <div className="pd-table">
          <table className="grid">
            <thead>
              <tr><th scope="col">Sensor</th><th scope="col">Station</th>
                <th scope="col">Files</th></tr>
            </thead>
            <tbody>
              {shownSensors.map((s: Sensor) => (
                <tr key={refOf(s)}>
                  <td><span className="pd-name">{s.name}</span></td>
                  <td className="muted">{s.station.name}</td>
                  <td>
                    <ul className="sd-files">
                      {(holders.get(refOf(s)) ?? []).map((d) => (
                        <li key={d.key}>
                          <FileBadge family={d.family} />
                          <a href={d.urls.original} {...(d.inline ? { target: '_blank', rel: 'noreferrer' } : { download: d.name })}>
                            {d.name}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {picker && (
        <SensorPicker projectId={projectId} mode={picker} sensors={sensors} docs={docs}
                      onDone={onChanged} onClose={() => setPicker(null)} />
      )}
    </div>
  )
}
